import exceljs from 'exceljs';

import { LoaderResult } from '../../types';

type XLSXLoaderSheetConfig = {
  headerRowNumber: number;
  dataStartRowNumber?: number;
};

type XLSXLoaderConfig =
  | {
      global: XLSXLoaderSheetConfig;
      hiddenSheets?: XLSXLoaderSheetConfig;
      processHiddenSheets?: boolean;
    }
  | { perSheet: Array<XLSXLoaderSheetConfig>; processHiddenSheets?: boolean };

const XLSX_LOADER_DEFAULT_CONFIG: XLSXLoaderConfig = {
  global: {
    headerRowNumber: 1,
    dataStartRowNumber: 2,
  },
  processHiddenSheets: false,
};
export const xlsxLoader = (config: XLSXLoaderConfig = XLSX_LOADER_DEFAULT_CONFIG) => {
  return async (file: any): Promise<LoaderResult> => {
    if (!file) {
      return Promise.reject();
    }

    try {
      const data = await readFileAsArrayBuffer(file);
      const resultData = await processData(data, config);
      return { data: resultData };
    } catch (error) {
      return Promise.reject(error);
    }
  };
};

function readFileAsArrayBuffer(file: any): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      resolve(new Uint8Array(e.target?.result as ArrayBuffer));
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}
const processData = async (data: Uint8Array, config: XLSXLoaderConfig) => {
  const workbook = await loadWorkbook(data);
  const excelInJSON: Record<string, Array<any>> = {};

  workbook.eachSheet((sheet, sheetNumber) => {
    // Skip hidden sheets if processHiddenSheets is false
    const processHiddenSheets = config.processHiddenSheets ?? false;
    if (!processHiddenSheets && sheet.state === 'hidden') {
      return;
    }

    const index = sheetNumber - 1;
    const { headerRowNumber, dataStartRowNumber } = getSheetConfig(config, index, sheet.state);
    const sheetHeaders = getSheetHeaders(sheet, sheet.state == 'hidden' ? 1 : headerRowNumber);

    excelInJSON[sheet.name] = extractSheetData(sheet, sheetHeaders, dataStartRowNumber);
  });

  return excelInJSON;
};

const loadWorkbook = async (data: Uint8Array): Promise<exceljs.Workbook> => {
  const workbook = new exceljs.Workbook();
  await workbook.xlsx.load(data, {
    ignoreNodes: [
      'dimension',
      'sheetViews',
      'sheetFormatPr',
      'cols',
      'autoFilter',
      'rowBreaks',
      'hyperlinks',
      'pageMargins',
      'dataValidations',
      'pageSetup',
      'headerFooter',
      'printOptions',
      'picture',
      'drawing',
      'sheetProtection',
      'tableParts',
      'conditionalFormatting',
      'extLst',
    ],
  });
  return workbook;
};

const getSheetConfig = (config: XLSXLoaderConfig, sheetIndex: number, sheetState: exceljs.WorksheetState) => {
  if (sheetState === 'hidden' && 'hiddenSheets' in config && config.hiddenSheets) {
    return {
      headerRowNumber: config.hiddenSheets.headerRowNumber,
      dataStartRowNumber: config.hiddenSheets.dataStartRowNumber ?? config.hiddenSheets.headerRowNumber + 1,
    };
  }
  if ('global' in config) {
    return {
      headerRowNumber: config.global.headerRowNumber,
      dataStartRowNumber: config.global.dataStartRowNumber ?? config.global.headerRowNumber + 1,
    };
  } else {
    const sheetConfig = config.perSheet[sheetIndex];
    return {
      headerRowNumber: sheetConfig?.headerRowNumber ?? 1,
      dataStartRowNumber: sheetConfig?.dataStartRowNumber ?? (sheetConfig?.headerRowNumber ?? 1) + 1,
    };
  }
};

const getSheetHeaders = (sheet: exceljs.Worksheet, headerRowNumber: number): Array<string | undefined> => {
  const rowValues = sheet.getRow(headerRowNumber).values as Array<string | undefined>;
  // Remove the first empty element (Excel arrays are 1-indexed)
  return rowValues.slice(1);
};

const getCellValue = (cell: exceljs.Cell): string | number | Date | null => {
  // Handle null/undefined cells
  if (!cell || cell.value === null || cell.value === undefined) {
    return null;
  }

  // Handle different cell value types
  switch (cell.type) {
    case exceljs.ValueType.Null:
      return null;

    case exceljs.ValueType.Merge:
      // For merged cells, return null since we can't access the master cell value here
      return null;

    case exceljs.ValueType.Number:
      return typeof cell.value === 'number' ? cell.value : parseFloat(String(cell.value)) || 0;

    case exceljs.ValueType.String:
      return String(cell.value);

    case exceljs.ValueType.Date:
      return cell.value instanceof Date ? cell.value : new Date(String(cell.value));

    case exceljs.ValueType.Hyperlink:
      // Return the hyperlink text, not the URL
      return typeof cell.value === 'object' && cell.value && 'text' in cell.value
        ? String(cell.value.text)
        : String(cell.value);

    case exceljs.ValueType.Formula:
      // For formula cells, return the calculated result
      if (cell.result !== null && cell.result !== undefined) {
        // Recursively handle the result based on its type
        if (typeof cell.result === 'number') {
          return cell.result;
        } else if (cell.result instanceof Date) {
          return cell.result;
        } else {
          return String(cell.result);
        }
      }
      // Formula with no result should be null, not empty string
      return null;

    case exceljs.ValueType.SharedString:
      return String(cell.value);

    case exceljs.ValueType.RichText:
      // For rich text, concatenate all text parts
      if (typeof cell.value === 'object' && cell.value && 'richText' in cell.value) {
        const richTextArray = cell.value.richText as Array<{ text: string }>;
        return richTextArray.map((rt) => rt.text).join('');
      }
      return String(cell.value);

    case exceljs.ValueType.Boolean:
      return String(cell.value);

    case exceljs.ValueType.Error:
      // Return error as string for debugging
      return String(cell.value);

    default:
      // For unknown types, check if the value exists
      if (cell.value === null || cell.value === undefined) {
        return null;
      }
      return String(cell.value);
  }
};

const extractSheetData = (
  sheet: exceljs.Worksheet,
  headers: Array<string | undefined>,
  dataStartRowNumber: number,
): Array<Record<string, string | Date | number | null>> => {
  const data: Array<Record<string, string | Date | number | null>> = [];

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber >= dataStartRowNumber) {
      const rowData: Record<string, string | Date | number | null> = {};
      for (let index = 0; index < headers.length; index++) {
        const header = headers[index];
        if (header !== null && header !== undefined) {
          // Get the cell and extract its value (handles formulas properly)
          // Excel columns are 1-indexed, so we add 1 to the index
          const cell = row.getCell(index + 1);
          const cellValue = getCellValue(cell);
          rowData[header] = cellValue;
        }
      }
      data.push(rowData);
    }
  });
  return data;
};
